// user_sim Agent — 교육용 모의 내담자 계정의 활동지 "자동 채우기" 요청 형식·설정·마치기·삭제 알림.
// 계약: docs/user-sim-agent.md. 스트림 실행(AG-UI)은 api/userSimAgent.ts
// REACT_APP_AGENT_USER_SIM_API_HOST 미설정 시 mock Agent (개발 fallback).

import { authHeaders } from "api/sse";

export const USER_SIM_HOST =
  process.env.REACT_APP_AGENT_USER_SIM_API_HOST || "";
// 번들에 들어가는 공개 값 — 엔드포인트 1차 차단용 (계약 1장)
const API_KEY = process.env.REACT_APP_AGENT_USER_SIM_API_KEY || "";

export const USER_SIM_DOMAIN = "user_sim";
export const USER_SIM_AGUI_ENDPOINT = "agui";
export const USER_SIM_COMPLETE_ENDPOINT = "complete";
export const USER_SIM_REMOVE_ENDPOINT = "remove";

export const userSimKeyHeaders = (): Record<string, string> =>
  API_KEY ? { "X-User-Sim-Key": API_KEY } : {};

// ===== 요청 =====

export type UserSimFill = "required" | "optional" | "none";

export type UserSimVisibleIf = { key: string; options: number[] } | null;

type UserSimFieldBase = {
  key: string; // 칸 식별자 — Agent 는 해석하지 않고 그대로 돌려준다
  block: number;
  fill: UserSimFill;
  name: string | null; // 활동지 데이터의 칸 이름 (예: "근거 1")
  label: string | null; // 칸 앞 이름표 (예: "요소 1 > 확인되는 사실")
  question: string | null; // 칸 직전 안내 문구
  visible_if: UserSimVisibleIf;
};

export type UserSimField =
  | (UserSimFieldBase & {
      input: "text";
      value: string | null;
      placeholder: string | null;
    })
  | (UserSimFieldBase & {
      input: "percentage";
      value: number | null;
      min: number;
      max: number;
      allow_zero: boolean;
      placeholder: string | null;
    })
  | (UserSimFieldBase & {
      input: "choice";
      value: number | null; // 선택지 index (0부터)
      options: string[];
    })
  | (UserSimFieldBase & {
      input: "multi_choice";
      value: number[] | null; // 고른 선택지 index 들 (0부터)
      options: string[];
      select_count: number | null; // 정확히 이만큼 골라야 함. null 이면 1개 이상
    })
  | (UserSimFieldBase & {
      input: "date" | "time";
      value: string | null; // "YYYY-MM-DD" | "HH:mm"
    });

export type UserSimItem =
  | { type: "header"; text: string }
  | { type: "text"; text: string }
  | { type: "label"; text: string }
  | { type: "value"; text: string }
  | { type: "field"; key: string };

export type UserSimBlock = {
  block: number;
  visible_if: UserSimVisibleIf;
  items: UserSimItem[];
};

export type UserSimRule =
  | {
      type: "auto_sum"; // auto_key = total − Σ keys, 0 보다 커야 한다
      keys: string[];
      auto_key: string;
      total: number;
    }
  | {
      type: "at_least_one"; // keys 중 하나 이상 채운다
      keys: string[];
    };

export interface UserSimStreamRequest {
  version: 1;
  task: {
    key: string;
    id: string;
    automatic_thought_id: string | null;
    patient_id: number | null;
    title: string;
    record_key: string | null; // 생각기록지 키 (trk). 활동지는 null
    sentiment: "negative" | "positive" | null; // 생각기록지 힘든 일/좋은 일. 활동지는 null
  };
  screen: UserSimBlock[];
  fields: UserSimField[];
  rules: UserSimRule[];
}

// 모의 내담자 알림(마치기·삭제)을 보낸 뒤 화면을 닫기까지 기다리는 최대 시간.
// 인앱에서 화면이 닫히면(navigate_back·removeTab) WebView 가 파괴되어 요청이 사라진다
export const USER_SIM_COMPLETE_WAIT_MS = 3000;

// mock 모드(호스트 미설정)에서는 보내지 않는다
async function postUserSim(endpoint: string, body: unknown) {
  if (!USER_SIM_HOST) return;
  const response = await fetch(
    `${USER_SIM_HOST}/${USER_SIM_DOMAIN}/${endpoint}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
        ...userSimKeyHeaders(),
      },
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    throw new Error(`user_sim ${endpoint} 실패 (${response.status})`);
  }
}

// 마치기 후 최종 값 전달 (계약 5장)
export function completeUserSim(req: UserSimStreamRequest) {
  return postUserSim(USER_SIM_COMPLETE_ENDPOINT, req);
}

export type UserSimRemoveRequest = {
  patient_id: number | null;
  record_key: string;
};

// 생각기록지 삭제 알림 (계약 5.1) — 지운 기록을 Agent 사례에서도 뺀다. 이미 없는 기록이어도 200
export function removeUserSim(req: UserSimRemoveRequest) {
  return postUserSim(USER_SIM_REMOVE_ENDPOINT, req);
}

// =========================
// Mock
// =========================

// 계약대로 채울 값 — 선택지는 첫 번째(들), 합계 규칙 칸은 자동 계산 칸이 0 이 되지 않게 나눈다
export function getMockValues(req: UserSimStreamRequest) {
  const sumRules = new Map(
    req.rules.flatMap((rule) =>
      rule.type === "auto_sum"
        ? rule.keys.map((key) => [key, rule] as const)
        : []
    )
  );
  // auto_sum 칸 몫 — 이미 쓴 칸(fill none) 값을 빼고 남은 칸끼리 나눈다
  const fieldOfKey = new Map(req.fields.map((field) => [field.key, field]));
  const getSumShare = (rule: Extract<UserSimRule, { type: "auto_sum" }>) => {
    const written = rule.keys
      .map((key) => fieldOfKey.get(key))
      .filter((field) => field?.fill === "none" && field.value !== null)
      .map((field) => field?.value as number);
    const rest = rule.total - 1 - written.reduce((a, b) => a + b, 0);
    return Math.floor(rest / (rule.keys.length - written.length));
  };
  const chosen = new Map<string, number[]>();
  req.fields.forEach((field) => {
    if (field.input === "choice" && field.value !== null) {
      chosen.set(field.key, [field.value]);
    } else if (field.input === "multi_choice" && field.value !== null) {
      chosen.set(field.key, field.value);
    }
  });

  return req.fields.flatMap((field, index) => {
    if (field.fill === "none") return [];
    const condition = field.visible_if;
    if (
      condition &&
      !condition.options.some((option) =>
        (chosen.get(condition.key) || []).includes(option)
      )
    ) {
      return [];
    }

    let value: string | number | number[];
    if (field.input === "choice") {
      value = 0;
      chosen.set(field.key, [value]);
    } else if (field.input === "multi_choice") {
      value = Array.from({ length: field.select_count ?? 1 }, (v, i) => i);
      chosen.set(field.key, value);
    } else if (field.input === "percentage") {
      const rule = sumRules.get(field.key);
      value = rule ? getSumShare(rule) : 50;
    } else if (field.name === "comment_reply") {
      value =
        "말씀해 주신 부분을 다시 생각해 봤어요. 그때는 그렇게 느꼈던 것 같아요.";
    } else if (field.name === "feedback") {
      value = "근거를 찾는 게 생각보다 어려웠어요. 이렇게 써도 괜찮을까요?";
    } else {
      value = `${field.label || field.name || `${index + 1}번째 칸`}에 대한 예시 답변이에요.`;
    }
    return [{ key: field.key, value }];
  });
}
